const MOBILE_BREAKPOINT = 680;
const BOT_LAUNCHER_SIZE = 60;
const DEFAULT_WIDGET_WIDTH = 420;
const DEFAULT_WIDGET_HEIGHT = 550;
const FIXED_WIDGET_MIN_WIDTH = 370;
const FIXED_WIDGET_MIN_HEIGHT = 425;
const MIN_WIDGET_WIDTH = 280;
const MIN_WIDGET_HEIGHT = 320;
const DESCRIPTION_BOX_WIDTH = 240;
const DEFAULT_DESCRIPTION_HEIGHT = 96;
const FLOATING_GAP = 16;
const FLOATING_OFFSET_DESKTOP = 24;
const FLOATING_OFFSET_MOBILE = 12;
const FLOATING_Z_INDEX = 2147483000;

const getFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getDescriptionHeight = (config = {}) => {
  const descHeight = Number(config.desc_height);
  return Number.isFinite(descHeight) && descHeight > 0
    ? descHeight
    : DEFAULT_DESCRIPTION_HEIGHT;
};

const getScaledValue = (value, scale, fallback = 0) =>
  getFiniteNumber(value, fallback) * scale;

export const getViewportSize = () => {
  if (typeof window === "undefined") {
    return { width: 0, height: 0 };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

export const resolveWidgetDimensions = (
  config = {},
  viewportWidth = 0,
  viewportHeight = 0,
) => {
  const configuredWidth = Math.max(
    getFiniteNumber(config.width, DEFAULT_WIDGET_WIDTH),
    MIN_WIDGET_WIDTH,
  );
  const configuredHeight = Math.max(
    getFiniteNumber(config.height, DEFAULT_WIDGET_HEIGHT),
    MIN_WIDGET_HEIGHT,
  );
  const safeViewportWidth = viewportWidth || configuredWidth;
  const safeViewportHeight = viewportHeight || configuredHeight;
  const availableWidth = Math.max(safeViewportWidth - 24, MIN_WIDGET_WIDTH);
  const availableHeight = Math.max(
    (safeViewportWidth <= MOBILE_BREAKPOINT
      ? safeViewportHeight * 0.76
      : safeViewportHeight * 0.78) || configuredHeight,
    MIN_WIDGET_HEIGHT,
  );

  return {
    width: Math.min(configuredWidth, availableWidth),
    height: Math.min(configuredHeight, availableHeight),
  };
};

const resolveFixedWidgetDimensions = (
  config = {},
  viewport = getViewportSize(),
  scaleX = 1,
  scaleY = 1,
) => {
  const configuredWidth = Math.max(
    getFiniteNumber(config.width, DEFAULT_WIDGET_WIDTH),
    FIXED_WIDGET_MIN_WIDTH,
  );
  const configuredHeight = Math.max(
    getFiniteNumber(config.height, DEFAULT_WIDGET_HEIGHT),
    FIXED_WIDGET_MIN_HEIGHT,
  );
  const scaledWidth = Math.max(
    getScaledValue(config.width, scaleX, configuredWidth),
    FIXED_WIDGET_MIN_WIDTH,
  );
  const scaledHeight = Math.max(
    getScaledValue(config.height, scaleY, configuredHeight),
    FIXED_WIDGET_MIN_HEIGHT,
  );

  return {
    width: Math.min(scaledWidth, Math.max(viewport.width - 10, FIXED_WIDGET_MIN_WIDTH)),
    height: Math.min(
      scaledHeight,
      Math.max(viewport.height - 10, FIXED_WIDGET_MIN_HEIGHT),
    ),
  };
};

const hasSavedPosition = (config = {}) =>
  Number.isFinite(Number(config.viewport_x)) &&
  Number.isFinite(Number(config.viewport_y));

export const getFixedEmbedPositionStyles = (
  config = {},
  viewport = getViewportSize(),
) => {
  if (!hasSavedPosition(config) || !viewport.width || !viewport.height) {
    return null;
  }

  const originalWidth = Math.max(
    getFiniteNumber(config.original_x, viewport.width),
    1,
  );
  const originalHeight = Math.max(
    getFiniteNumber(config.original_y, viewport.height),
    1,
  );
  const scaleX = viewport.width / originalWidth;
  const scaleY = viewport.height / originalHeight;
  const { width, height } = resolveFixedWidgetDimensions(
    config,
    viewport,
    scaleX,
    scaleY,
  );
  const descHeight = getDescriptionHeight(config);
  const launcherX = clamp(
    getScaledValue(config.viewport_x, scaleX, viewport.width - BOT_LAUNCHER_SIZE),
    0,
    Math.max(viewport.width - BOT_LAUNCHER_SIZE, 0),
  );
  const launcherY = clamp(
    getScaledValue(
      config.viewport_y,
      scaleY,
      viewport.height - BOT_LAUNCHER_SIZE,
    ),
    0,
    Math.max(viewport.height - BOT_LAUNCHER_SIZE, 0),
  );
  const isDescriptionOnRight =
    getFiniteNumber(config.desc_box_x, launcherX) >=
    getFiniteNumber(config.viewport_x, launcherX);
  const isDescriptionAbove =
    getFiniteNumber(config.viewport_y, launcherY) >=
    getFiniteNumber(config.desc_box_y, launcherY - descHeight);
  const hasSavedDescriptionPosition =
    Number.isFinite(Number(config.desc_box_x)) &&
    Number.isFinite(Number(config.desc_box_y));

  let descriptionX = clamp(
    getScaledValue(config.desc_box_x, scaleX, launcherX),
    0,
    Math.max(viewport.width - DESCRIPTION_BOX_WIDTH, 0),
  );
  if (!hasSavedDescriptionPosition) {
    descriptionX = launcherX + 45 - DESCRIPTION_BOX_WIDTH;
    if (isDescriptionOnRight) {
      descriptionX = launcherX;
      if (descriptionX + DESCRIPTION_BOX_WIDTH > viewport.width) {
        descriptionX = launcherX + 45 - DESCRIPTION_BOX_WIDTH;
      }
    }
  }

  let chatBoxX = clamp(
    getScaledValue(config.chat_box_x, scaleX, launcherX + 45 - width),
    0,
    Math.max(viewport.width - width, 0),
  );
  if (!Number.isFinite(Number(config.chat_box_x))) {
    chatBoxX = launcherX + 45 - width;
    if (isDescriptionOnRight) {
      chatBoxX = launcherX;
      if (chatBoxX + width > viewport.width) {
        chatBoxX = launcherX + 45 - width;
      }
    }
  }

  let descriptionY = clamp(
    getScaledValue(config.desc_box_y, scaleY, launcherY + BOT_LAUNCHER_SIZE),
    0,
    Math.max(viewport.height - descHeight, 0),
  );
  if (!hasSavedDescriptionPosition) {
    descriptionY = launcherY + BOT_LAUNCHER_SIZE;
    if (isDescriptionAbove) {
      descriptionY = launcherY - descHeight;
      if (descriptionY + descHeight > viewport.height) {
        descriptionY = launcherY + BOT_LAUNCHER_SIZE;
      }
    } else if (descriptionY + descHeight > viewport.height) {
      const overflow = descriptionY + descHeight - viewport.height;
      descriptionY = Math.max(viewport.height - descHeight - 5, 0);
      const nextLauncherY = Math.max(launcherY - overflow - 5, 0);
      if (nextLauncherY !== launcherY) {
        return getFixedEmbedPositionStyles(
          {
            ...config,
            viewport_y: nextLauncherY / scaleY,
          },
          viewport,
        );
      }
    }
  }

  let chatBoxY = clamp(
    getScaledValue(config.chat_box_y, scaleY, launcherY),
    0,
    Math.max(viewport.height - height, 0),
  );
  if (!Number.isFinite(Number(config.chat_box_y))) {
    chatBoxY = launcherY;
    if (isDescriptionAbove) {
      chatBoxY = launcherY + BOT_LAUNCHER_SIZE - height;
      if (chatBoxY + height > viewport.height) {
        chatBoxY = launcherY;
      }
    } else if (chatBoxY + height > viewport.height) {
      chatBoxY = viewport.height - height - 5;
    }
  }

  descriptionX = clamp(
    descriptionX,
    0,
    Math.max(viewport.width - DESCRIPTION_BOX_WIDTH, 0),
  );
  descriptionY = clamp(
    descriptionY,
    0,
    Math.max(viewport.height - descHeight, 0),
  );
  chatBoxX = clamp(chatBoxX, 0, Math.max(viewport.width - width, 0));
  chatBoxY = clamp(chatBoxY, 0, Math.max(viewport.height - height, 0));

  return {
    description: {
      left: `${descriptionX}px`,
      maxWidth: `${DESCRIPTION_BOX_WIDTH}px`,
      position: "fixed",
      top: `${descriptionY}px`,
      width: `${DESCRIPTION_BOX_WIDTH}px`,
      zIndex: FLOATING_Z_INDEX,
    },
    launcher: {
      left: `${launcherX}px`,
      position: "fixed",
      top: `${launcherY}px`,
      zIndex: FLOATING_Z_INDEX,
    },
    window: {
      height: `${height}px`,
      left: `${chatBoxX}px`,
      maxHeight: `${height}px`,
      position: "fixed",
      top: `${chatBoxY}px`,
      width: `${width}px`,
      zIndex: FLOATING_Z_INDEX,
    },
  };
};

export const getFloatingWidgetPositionStyles = (
  config = {},
  viewport = getViewportSize(),
) => {
  if (!viewport.width || !viewport.height) {
    return null;
  }

  const { width, height } = resolveWidgetDimensions(
    config,
    viewport.width,
    viewport.height,
  );
  const offset =
    viewport.width <= MOBILE_BREAKPOINT
      ? FLOATING_OFFSET_MOBILE
      : FLOATING_OFFSET_DESKTOP;
  const launcherBottom = offset;
  const launcherRight = offset;
  const windowBottom = launcherBottom + BOT_LAUNCHER_SIZE + FLOATING_GAP;
  const descriptionBottom = launcherBottom + BOT_LAUNCHER_SIZE + FLOATING_GAP;

  return {
    description: {
      bottom: `${descriptionBottom}px`,
      maxWidth: `${Math.min(DESCRIPTION_BOX_WIDTH, width)}px`,
      position: "fixed",
      right: `${offset}px`,
      width: `${Math.min(DESCRIPTION_BOX_WIDTH, width)}px`,
      zIndex: FLOATING_Z_INDEX,
    },
    launcher: {
      bottom: `${launcherBottom}px`,
      position: "fixed",
      right: `${launcherRight}px`,
      zIndex: FLOATING_Z_INDEX,
    },
    window: {
      bottom: `${windowBottom}px`,
      height: `${height}px`,
      maxHeight: `${height}px`,
      position: "fixed",
      right: `${offset}px`,
      width: `${width}px`,
      zIndex: FLOATING_Z_INDEX,
    },
  };
};
